import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, RefreshCw, Send } from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';

const REGISTRIES = ['verra', 'gold_standard', 'cdm', 'national', 'custom'];
const STATUSES = ['draft', 'submitted', 'verified', 'rejected'];

export default function RegistryPanel() {
  const { showSuccess, showError } = useAlert();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mrv/registry/submissions`);
      setSubs(res.data.submissions || []);
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async (id, status) => {
    try {
      const body = { status };
      if (status === 'submitted') body.submitted_at = new Date().toISOString();
      if (status === 'verified') body.verified_at = new Date().toISOString();
      await axios.patch(`${API}/mrv/registry/submissions/${id}`, body);
      showSuccess(`Marked ${status}`);
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Update failed');
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await axios.delete(`${API}/mrv/registry/submissions/${id}`);
      showSuccess('Submission deleted');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Registry submissions</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowCreate(s => !s)}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> New submission
          </button>
          <button onClick={fetchAll} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateSubForm onCreated={() => { setShowCreate(false); fetchAll(); }} onCancel={() => setShowCreate(false)} />
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <th className="px-3 py-2 font-semibold text-gray-700">Registry</th>
              <th className="px-3 py-2 font-semibold text-gray-700">External Ref</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Status</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Submitted</th>
              <th className="px-3 py-2 font-semibold text-gray-700">Verified</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {subs.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No submissions yet</td></tr>
            )}
            {subs.map(s => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium uppercase">{s.registry}</td>
                <td className="px-3 py-2 text-gray-600 font-mono text-xs">{s.external_ref || '—'}</td>
                <td className="px-3 py-2">
                  <select value={s.status} onChange={e => updateStatus(s.id, e.target.value)}
                          className="px-2 py-0.5 text-xs border rounded bg-white">
                    {STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {s.verified_at ? new Date(s.verified_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => deleteSub(s.id)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateSubForm({ onCreated, onCancel }) {
  const { showError, showSuccess } = useAlert();
  const [form, setForm] = useState({ registry: 'verra', external_ref: '', report_id: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/mrv/registry/submissions`, {
        registry: form.registry,
        external_ref: form.external_ref || null,
        report_id: form.report_id || null,
        notes: form.notes || null,
      });
      showSuccess('Submission created');
      onCreated();
    } catch (e) {
      showError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 p-4 border border-emerald-200 bg-emerald-50 rounded-md">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <select value={form.registry}
                onChange={e => setForm(f => ({ ...f, registry: e.target.value }))}
                className="px-2 py-1 text-sm border rounded">
          {REGISTRIES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input placeholder="External ref" value={form.external_ref}
               onChange={e => setForm(f => ({ ...f, external_ref: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Report ID (optional)" value={form.report_id}
               onChange={e => setForm(f => ({ ...f, report_id: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
        <input placeholder="Notes" value={form.notes}
               onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
               className="px-2 py-1 text-sm border rounded" />
      </div>
      <div className="mt-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button onClick={submit} disabled={saving}
                className="px-3 py-1 bg-emerald-600 text-white rounded text-sm font-medium disabled:bg-gray-300">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
